import api from "./api";
import { API_ENDPOINTS } from "../utils/constants";

/**
 * propertyService
 *
 * Purpose: Defines property API method signatures.
 * Used by: PropertyContext and property management components.
 */
export const propertyService = {
  list: async (filters = {}, config = {}) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.LIST, {
      params: filters,
      ...config,
    });
    return response.data;
  },
  // id/name/location only, unpaginated — for filter dropdowns and pickers
  // that don't need a property's full record. Avoids pulling every
  // property's media/documents (see PropertyOptionsView) just to fill a
  // <select>.
  listOptions: async (config = {}) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.OPTIONS, config);
    return response.data;
  },
  // Home-screen quick search: up to 10 id/name/location/type matches plus
  // the total count — same matching as list()'s `search` filter.
  search: async (query, config = {}) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.SEARCH, {
      params: { q: query },
      ...config,
    });
    return response.data;
  },
  // Every property matching `filters`, unpaginated, as a slim coordinates +
  // marker payload — the Map view shows the whole portfolio at once and
  // can't page. Shares the list endpoint's filters (status, group, …).
  listForMap: async (filters = {}, config = {}) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.MAP, {
      params: filters,
      ...config,
    });
    return response.data;
  },
  // Portfolio-wide dashboard figures counted server-side, plus only the few
  // watchlist rows the dashboard shows — see apps/properties/dashboard.py.
  getDashboardSummary: async (config = {}) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.DASHBOARD_SUMMARY, config);
    return response.data;
  },
  get: async (propertyId) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.DETAIL(propertyId));
    return response.data;
  },
  create: async (data) => {
    const response = await api.post(API_ENDPOINTS.PROPERTIES.LIST, data);
    return response.data;
  },
  update: async (propertyId, data) => {
    const response = await api.patch(
      API_ENDPOINTS.PROPERTIES.DETAIL(propertyId),
      data,
    );
    return response.data;
  },
  delete: async (propertyId) => {
    const response = await api.delete(
      API_ENDPOINTS.PROPERTIES.DETAIL(propertyId),
    );
    return response.data;
  },
  getNextSrNo: async () => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.NEXT_SR_NO);
    return response.data;
  },
  getAllotmentOptions: async () => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.ALLOTMENT_OPTIONS);
    return response.data;
  },
  // Follows a Google Maps link (usually a maps.app.goo.gl shortlink)
  // server-side and returns { latitude, longitude, resolved }. The browser
  // can't do this itself — Google Maps sends no CORS headers.
  resolveMapLink: async (link) => {
    const response = await api.post(API_ENDPOINTS.PROPERTIES.RESOLVE_MAP_LINK, {
      google_maps_link: link,
    });
    return response.data;
  },
  listSubProperties: async (propertyId) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.SUB_PROPERTIES(propertyId));
    return response.data;
  },
  createSubProperty: async (propertyId, formData) => {
    const response = await api.post(
      API_ENDPOINTS.PROPERTIES.SUB_PROPERTIES(propertyId),
      formData,
      { headers: { "Content-Type": undefined } },
    );
    return response.data;
  },
  updateSubProperty: async (propertyId, subPropertyId, formData) => {
    const response = await api.patch(
      API_ENDPOINTS.PROPERTIES.SUB_PROPERTY_DETAIL(propertyId, subPropertyId),
      formData,
      { headers: { "Content-Type": undefined } },
    );
    return response.data;
  },
  deleteSubProperty: async (propertyId, subPropertyId) => {
    await api.delete(API_ENDPOINTS.PROPERTIES.SUB_PROPERTY_DETAIL(propertyId, subPropertyId));
  },
  listTenancies: async (propertyId) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.TENANCIES(propertyId));
    return response.data;
  },
  createTenancy: async (propertyId, data) => {
    const response = await api.post(API_ENDPOINTS.PROPERTIES.TENANCIES(propertyId), data);
    return response.data;
  },
  updateTenancy: async (propertyId, tenancyId, data) => {
    const response = await api.patch(
      API_ENDPOINTS.PROPERTIES.TENANCY_DETAIL(propertyId, tenancyId),
      data,
    );
    return response.data;
  },
  deleteTenancy: async (propertyId, tenancyId) => {
    await api.delete(API_ENDPOINTS.PROPERTIES.TENANCY_DETAIL(propertyId, tenancyId));
  },
  listExpenses: async (propertyId) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.EXPENSES(propertyId));
    return response.data;
  },
  // For the Expenses tab badge — a number, not every expense record (with
  // every SmartScan flag) on the property. See PropertyExpenseListCreateView.get's
  // count_only path.
  getExpenseCount: async (propertyId) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.EXPENSES(propertyId), {
      params: { count_only: 1 },
    });
    return response.data.count;
  },
  createExpense: async (propertyId, data) => {
    const isFormData = data instanceof FormData;
    const response = await api.post(
      API_ENDPOINTS.PROPERTIES.EXPENSES(propertyId),
      data,
      isFormData ? { headers: { "Content-Type": undefined } } : undefined,
    );
    return response.data;
  },
  updateExpense: async (propertyId, expenseId, data) => {
    const isFormData = data instanceof FormData;
    const response = await api.patch(
      API_ENDPOINTS.PROPERTIES.EXPENSE_DETAIL(propertyId, expenseId),
      data,
      isFormData ? { headers: { "Content-Type": undefined } } : undefined,
    );
    return response.data;
  },
  deleteExpense: async (propertyId, expenseId) => {
    await api.delete(API_ENDPOINTS.PROPERTIES.EXPENSE_DETAIL(propertyId, expenseId));
  },
  // note / files are optional proof; with neither it's the plain one-click check.
  completeExpenseCheck: async (propertyId, expenseId, checkType, { note = "", files = [] } = {}) => {
    const url = API_ENDPOINTS.PROPERTIES.EXPENSE_CHECK(propertyId, expenseId, checkType);
    if (!note && files.length === 0) {
      const response = await api.post(url, {});
      return response.data;
    }
    const formData = new FormData();
    formData.append("note", note);
    files.forEach((file) => formData.append("files", file));
    const response = await api.post(url, formData, { headers: { "Content-Type": undefined } });
    return response.data;
  },
  // checkType: "check_i" | "check_ii". Returns the updated expense.
  addExpenseCheckFiles: async (propertyId, expenseId, checkType, files) => {
    const formData = new FormData();
    formData.append("check_type", checkType);
    files.forEach((file) => formData.append("files", file));
    const response = await api.post(API_ENDPOINTS.PROPERTIES.EXPENSE_CHECK_FILES(propertyId, expenseId), formData, {
      headers: { "Content-Type": undefined },
    });
    return response.data;
  },
  deleteExpenseCheckFile: async (propertyId, expenseId, fileId) => {
    const response = await api.delete(
      API_ENDPOINTS.PROPERTIES.EXPENSE_CHECK_FILE_DETAIL(propertyId, expenseId, fileId),
    );
    return response.data;
  },
  listExpenseOptions: async (listType) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.EXPENSE_OPTIONS, {
      params: { list_type: listType },
    });
    return response.data;
  },
  createExpenseOption: async (listType, value) => {
    const response = await api.post(API_ENDPOINTS.PROPERTIES.EXPENSE_OPTIONS, {
      list_type: listType,
      value,
    });
    return response.data;
  },
  updateExpenseOption: async (optionId, value) => {
    const response = await api.patch(API_ENDPOINTS.PROPERTIES.EXPENSE_OPTION_DETAIL(optionId), {
      value,
    });
    return response.data;
  },
  deleteExpenseOption: async (optionId) => {
    await api.delete(API_ENDPOINTS.PROPERTIES.EXPENSE_OPTION_DETAIL(optionId));
  },
  listExpenseSummaryByProperty: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.EXPENSE_SUMMARY_BY_PROPERTY, { params });
    return response.data;
  },
  getExpenseFlagSummary: async (propertyId) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.EXPENSE_FLAG_SUMMARY(propertyId));
    return response.data;
  },
  rescanExpenses: async (propertyId) => {
    // Returns 202 immediately — SmartScan runs in the background, so the
    // default axios timeout is fine here (no HEAVY_TIMEOUT_MS override).
    const response = await api.post(API_ENDPOINTS.PROPERTIES.EXPENSE_RESCAN(propertyId), {});
    return response.data;
  },
  listExpenseQuotations: async (propertyId, expenseId) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.EXPENSE_QUOTATIONS(propertyId, expenseId));
    return response.data;
  },
  // formData: vendor_name, amount?, note?, files (one or more)
  createExpenseQuotation: async (propertyId, expenseId, formData) => {
    const response = await api.post(
      API_ENDPOINTS.PROPERTIES.EXPENSE_QUOTATIONS(propertyId, expenseId),
      formData,
      { headers: { "Content-Type": undefined } },
    );
    return response.data;
  },
  updateExpenseQuotation: async (propertyId, expenseId, quotationId, data) => {
    const response = await api.patch(
      API_ENDPOINTS.PROPERTIES.EXPENSE_QUOTATION_DETAIL(propertyId, expenseId, quotationId),
      data,
    );
    return response.data;
  },
  deleteExpenseQuotation: async (propertyId, expenseId, quotationId) => {
    await api.delete(API_ENDPOINTS.PROPERTIES.EXPENSE_QUOTATION_DETAIL(propertyId, expenseId, quotationId));
  },
  // Returns { expense (with budget_warnings), quotation }; the expense is now Recorded.
  approveExpenseQuotation: async (propertyId, expenseId, quotationId) => {
    const response = await api.post(
      API_ENDPOINTS.PROPERTIES.EXPENSE_QUOTATION_APPROVE(propertyId, expenseId, quotationId),
      {},
    );
    return response.data;
  },
  // formData: files (one or more), revision_note?, amount? — a new revision
  addExpenseQuotationRevision: async (propertyId, expenseId, quotationId, formData) => {
    const response = await api.post(
      API_ENDPOINTS.PROPERTIES.EXPENSE_QUOTATION_FILES(propertyId, expenseId, quotationId),
      formData,
      { headers: { "Content-Type": undefined } },
    );
    return response.data;
  },
  deleteExpenseQuotationFile: async (propertyId, expenseId, quotationId, fileId) => {
    await api.delete(
      API_ENDPOINTS.PROPERTIES.EXPENSE_QUOTATION_FILE_DETAIL(propertyId, expenseId, quotationId, fileId),
    );
  },
  getBudget: async (propertyId) => {
    const response = await api.get(API_ENDPOINTS.PROPERTIES.BUDGET(propertyId));
    return response.data;
  },
  updateBudget: async (propertyId, data) => {
    const response = await api.patch(API_ENDPOINTS.PROPERTIES.BUDGET(propertyId), data);
    return response.data;
  },
  updateExpenseFlag: async (propertyId, expenseId, flagId, { status: flagStatus, statusNote }) => {
    const response = await api.patch(
      API_ENDPOINTS.PROPERTIES.EXPENSE_FLAG_DETAIL(propertyId, expenseId, flagId),
      { status: flagStatus, status_note: statusNote },
    );
    return response.data;
  },
};

export default propertyService;
